import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Search, Instagram, Facebook } from "lucide-react";
import SmartLink from "./SmartLink";

type NavItem = {
  label: string;
  href?: string;
  children?: { label: string; href: string }[];
};

const navItems: NavItem[] = [
  { label: "Destinations", href: "/go/destinations-nav" },
  {
    label: "Registration",
    children: [
      { label: "2027 Registration", href: "/registration/" },
      { label: "Availability", href: "/availability/" },
      { label: "Become", href: "https://become.hxp.org" },
    ],
  },
  {
    label: "Who Can Go?",
    children: [
      { label: "Youth Builders", href: "/youth-builders/" },
      { label: "Parent Builders", href: "/parent-builders/" },
      { label: "Trip Leaders", href: "/trip-leaders/" },
      { label: "Parents at Home", href: "/parents-at-home/" },
    ],
  },
  {
    label: "History",
    children: [
      { label: "Inside HXP", href: "/about/" },
      { label: "Impact Report", href: "/2025-impact-report/" },
    ],
  },
  {
    label: "Answers",
    children: [
      { label: "FAQ", href: "/faq/" },
      { label: "Blog", href: "/blog/" },
      { label: "Paying For Your Trip", href: "/paying-for-your-trip/" },
      { label: "Policies", href: "/policies/" },
      { label: "Join our mailing list", href: "https://mailchi.mp/hxp/humanitarian-experience-mailing-list" },
    ],
  },
  { label: "Swag Shop", href: "https://shop.hxp.org/" },
];

/** Routes whose hero is dark (video/photo) — header starts transparent over it. */
const DARK_HERO_ROUTES = new Set([
  "/",
  "/about",
  "/youth-builders",
  "/parent-builders",
  "/trip-leaders",
  "/parents-at-home",
  "/registration",
  "/availability", // same photo banner as /registration (marketing, Nov 2026)
  "/2025-impact-report", // photo banner like the Who Can Go pages (marketing, Nov 2026)
]);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const darkHero = DARK_HERO_ROUTES.has(pathname.replace(/\/+$/, "") || "/");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setMobileExpanded(null);
  }, []);

  // Three clean states — no blend tricks:
  // overlay  : transparent over a dark hero (white text, white logo)
  // solid    : cream bar, 3px sand rule, red logo (the Become bar)
  // open     : dark mobile drawer
  const overlay = darkHero && !scrolled && !open;

  const itemClass = open
    ? "text-white hover:text-primary"
    : overlay
    ? "text-white/90 hover:text-white"
    : "text-foreground hover:text-primary";

  return (
    <nav
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        open
          ? "bg-foreground shadow-lg"
          : overlay
          ? "bg-gradient-to-b from-black/60 via-black/30 to-transparent"
          : "border-b-[3px] border-hxp-header-border bg-header shadow-sm"
      }`}
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-4 py-2.5 lg:px-6">
        {/* True HXP logo — goes white over dark heroes */}
        <SmartLink
          href="/"
          aria-label="HXP — Humanitarian Experience"
          className="flex items-center gap-2 transition-opacity hover:opacity-90"
        >
          <img
            src="/images/brand/hxp-logo-red.png"
            width={160}
            height={96}
            alt="HXP"
            className={`h-6 w-auto transition-[filter] duration-300 md:h-8 ${
              overlay || open ? "brightness-0 invert" : ""
            }`}
          />
          <img
            src="/images/brand/hxp-tm.png"
            width={480}
            height={96}
            alt="Humanitarian Experience"
            className={`h-6 w-auto transition-[filter] duration-300 md:h-8 ${
              overlay || open ? "brightness-0 invert" : ""
            }`}
          />
        </SmartLink>

        {/* Desktop nav */}
        <div className="hidden items-center gap-6 lg:flex lg:gap-8 xl:gap-10">
          {navItems.map((item) =>
            item.children ? (
              <div key={item.label} className="group relative">
                <button
                  className={`font-subhead inline-flex items-center gap-1 text-xs font-normal uppercase tracking-[0.08em] transition-colors ${itemClass}`}
                >
                  {item.label}
                  <ChevronDown
                    size={12}
                    className="opacity-60 transition-transform group-hover:rotate-180"
                  />
                </button>
                {/* Dropdown — always the cream panel with sand border */}
                <div className="invisible absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                  <div className="overflow-hidden rounded-sm border border-hxp-sand-dark bg-header shadow-xl">
                    {item.children.map((child) => (
                      <SmartLink
                        key={child.href}
                        href={child.href}
                        className="font-body block px-4 py-3 text-sm text-foreground transition-colors hover:bg-primary hover:text-white"
                      >
                        {child.label}
                      </SmartLink>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <SmartLink
                key={item.label}
                href={item.href!}
                className={`font-subhead text-xs font-normal uppercase tracking-[0.08em] transition-colors ${itemClass}`}
              >
                {item.label}
              </SmartLink>
            )
          )}
        </div>

        {/* Desktop right cluster */}
        <div className="hidden items-center gap-4 lg:flex">
          <a
            href="https://www.instagram.com/hxp.humanitarianexperience/"
            target="_self"
            aria-label="Instagram"
            className={`transition-colors ${itemClass}`}
          >
            <Instagram size={17} />
          </a>
          <a
            href="https://www.facebook.com/humanitarianexperience/"
            target="_self"
            aria-label="Facebook"
            className={`transition-colors ${itemClass}`}
          >
            <Facebook size={17} />
          </a>
          <a
            href="/go/account-login"
            target="_self"
            className={`btn-caps rounded-sm px-4 py-1.5 text-[11px] transition-colors ${
              overlay || open
                ? "border border-white/50 text-white hover:bg-white hover:text-foreground"
                : "bg-hxp-red-dark text-white hover:bg-[#9d382e]"
            }`}
          >
            Account
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          className={`lg:hidden ${itemClass}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
          aria-expanded={open}
          aria-controls="mobile-menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div id="mobile-menu" className="max-h-[85vh] overflow-y-auto bg-foreground px-5 pb-8 pt-2 lg:hidden">
          {navItems.map((item) =>
            item.children ? (
              <div key={item.label} className="border-b border-white/10">
                <button
                  onClick={() =>
                    setMobileExpanded((cur) => (cur === item.label ? null : item.label))
                  }
                  className="font-subhead flex w-full items-center justify-between py-3.5 text-base font-normal uppercase tracking-[0.08em] text-white transition-colors hover:text-primary"
                >
                  {item.label}
                  <ChevronDown
                    size={18}
                    className={`transition-transform ${
                      mobileExpanded === item.label ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {mobileExpanded === item.label && (
                  <div className="pb-3 pl-3">
                    {item.children.map((child) => (
                      <SmartLink
                        key={child.href}
                        href={child.href}
                        onClick={close}
                        className="font-body block py-2 text-sm text-white/75 transition-colors hover:text-primary"
                      >
                        {child.label}
                      </SmartLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <SmartLink
                key={item.label}
                href={item.href!}
                onClick={close}
                className="font-subhead block border-b border-white/10 py-3.5 text-base font-normal uppercase tracking-[0.08em] text-white transition-colors hover:text-primary"
              >
                {item.label}
              </SmartLink>
            )
          )}

          <div className="mt-5 flex items-center gap-4">
            <a
              href="https://www.instagram.com/hxp.humanitarianexperience/"
              target="_self"
              aria-label="Instagram"
              className="text-white transition-colors hover:text-primary"
            >
              <Instagram size={20} />
            </a>
            <a
              href="https://www.facebook.com/humanitarianexperience/"
              target="_self"
              aria-label="Facebook"
              className="text-white transition-colors hover:text-primary"
            >
              <Facebook size={20} />
            </a>
            <a
              href="/go/account-login"
              target="_self"
              className="btn-caps ml-auto rounded-sm bg-hxp-red-dark px-5 py-2 text-xs text-white transition-colors hover:bg-[#9d382e]"
            >
              Account
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

/** Spacer so content on light-hero pages doesn't slide under the fixed bar. */
export const NavbarSpacer = () => {
  const { pathname } = useLocation();
  const darkHero = DARK_HERO_ROUTES.has(pathname.replace(/\/+$/, "") || "/");
  if (darkHero) return null;
  return <div className="h-[59px] md:h-[63px]" aria-hidden="true" />;
};

export default Navbar;
